import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { SabhaCallService, SabhaChat, SabhaChatService, SabhaRoomService } from '@soti/sabha-components';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { filter, first, map, mergeMap, shareReplay, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { XSightRoom } from '../models/xsight-room/xsight-room';
import { XSightRoomFactory } from '../models/xsight-room/xsight-room-factory';
import { isRunning } from '../sabha-utils';
import * as fromSabhaActions from '../store/actions';
import { DeviceChatDialogView, SabhaState } from '../store/reducers';
import * as fromSabha from '../store/selectors';
import { SabhaRoom } from '@soti/sabha-components/lib/room/room.model';

/**
 * Show timeout prompt before value in milliseconds
 */
export const TIMEOUT_PROMPT_BEFORE: number = 1 * 60000;

@Injectable({
  providedIn: 'root',
})
export class XsightRoomService implements OnDestroy {

  private _rooms: Map<string, XSightRoom> = new Map();
  private _rooms$ = new BehaviorSubject<Array<XSightRoom>>([]);

  private _onDestroy$ = new Subject<void>();
  private readonly hasActiveRings = new BehaviorSubject(false);
  public activeRcRoom: XSightRoom;
  rooms$: Observable<Array<XSightRoom>> = this._rooms$.asObservable();
  activeRoom$: Observable<XSightRoom>;
  activeRcRoom$: Observable<XSightRoom>;
  activeCallRoom$: Observable<XSightRoom>;

  constructor(
    private store: Store<SabhaState>,
    private chatService: SabhaChatService,
    private callService: SabhaCallService,
    private roomService: SabhaRoomService,
    private roomFactory: XSightRoomFactory
  ) {
    this.roomService.rooms$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(rooms => {
        const newRooms = new Array<XSightRoom>();
        const newRoomMap = new Map<string, XSightRoom>();
        for (const sabhaRoom of rooms) {
          let xSightRoom = this._rooms.get(sabhaRoom.id);
          if (!xSightRoom) {
            xSightRoom = this.roomFactory.create(sabhaRoom);
          } else {
            xSightRoom.sabhaRoom = sabhaRoom;
          }
          newRoomMap.set(sabhaRoom.id, xSightRoom);
          newRooms.push(xSightRoom);
        }

        // Dispose of rooms which sabha does not have anymore
        const roomIdsToDispose = Array.from(this._rooms.keys()).filter(roomId => !newRoomMap.has(roomId));
        for (const roomId of roomIdsToDispose) {
          this._rooms.get(roomId)?.dispose();
          this.store.dispatch(new fromSabhaActions.RemoveInactiveRoomAction(roomId));
        }

        this._rooms = newRoomMap;
        this._rooms$.next(newRooms);
      });

    this.activeRoom$ = this.store.select(fromSabha.getActiveRoomId).pipe(
      switchMap(activeRoomId => of(activeRoomId).pipe(withLatestFrom(this.rooms$))),
      map(([activeRoomId, rooms]) => rooms.find(r => r.id === activeRoomId)),
      shareReplay(1)
    );
    this.activeRcRoom$ = this.rooms$.pipe(
      switchMap(rooms => {
        const observables = rooms.map(room => room.isRcing$);
        return combineLatest(observables).pipe(withLatestFrom(of(rooms)));
      }),
      map(([isRcingRooms, rooms]) => {
        const isRcingIndex = isRcingRooms.findIndex(isRcing => isRcing === true);
        if (isRcingIndex >= 0) {
          return rooms[isRcingIndex];
        }
      }),
      shareReplay(1)
    );
    this.activeCallRoom$ = this.store.select(fromSabha.getActiveCallRoomId).pipe(
      withLatestFrom(this.rooms$),
      map(([activeCallRoomId, rooms]) => rooms.find(r => r.id === activeCallRoomId)),
      shareReplay(1)
    );

    this.activeRcRoom$.pipe(takeUntil(this._onDestroy$)).subscribe(rc => {
      this.activeRcRoom = rc;
    });

    this.store
      .select(fromSabha.getRingRoomIds)
      .pipe(
        takeUntil(this._onDestroy$),
        map(([ringRoomIds]) => {
          return !!ringRoomIds && ringRoomIds.length > 0;
        })
      )
      .subscribe(this.hasActiveRings);
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  public getXsightRoomById$(roomId: string): Observable<XSightRoom> {
    return this.rooms$.pipe(
      filter(rooms => !!rooms && rooms.findIndex(r => r.id === roomId) > -1),
      map(rooms => rooms.find(r => r.id === roomId))
    );
  }

  public getXsightRoomByDevice$(deviceId: string): Observable<XSightRoom> {
    return this.rooms$.pipe(
      mergeMap(rooms => rooms),
      switchMap(room => {
        return room.getSabhaDeviceParticipant$().pipe(
          filter(p => p.externalId === deviceId),
          map(_ => room)
        );
      }),
      filter(room => !!room)
    );
  }

  public hasActionCallOrRing = (): boolean => {
    return isRunning(this.callService.currentCall) || this.hasActiveRings.value;
  };

  public setChatRoomAsActive(roomId: string): void {
    const chat = this._rooms.get(roomId)?.chatContext$?.value?.room as SabhaChat;
    if (!!chat) {
      this.chatService.activeRoom = chat;
    }
  }

  public setChatRoomAsInactive(roomId: string): void {
    if (roomId && this.chatService.activeRoom?.roomId === roomId) {
      this.chatService.activeRoom = null;
    }
  }

  public updateRoomActivity(roomId: string): void {
    this.roomService.updateActivity(roomId);
  }

  public onRoomRemoved$(roomId: string): Observable<SabhaRoom> {
    const roomRemoved$ = this.roomService.roomRemoved$.pipe(filter(r => r.id === roomId), first());
    return roomRemoved$;
  }

  public hasActiveCall(): boolean {
    for (const room of this._rooms.values()) {
      if (room.callContext && !room.callContext.isOnHold) {
        return true;
      }
    }
    return false;
  }

  public getCallForRoom(roomId: string) {
    return this._rooms.get(roomId).callContext;
  }

  public async holdCall(roomId: string) {
    await this.callService.onHoldByRoomId(roomId);
  }

  public async resumeCall(roomId: string) {
    await this.callService.resumeCallByRoomId(roomId);
  }

  public selectRoom(roomId: string): void {
    this.store.dispatch(new fromSabhaActions.SelectCurrentViewAction(DeviceChatDialogView.MainView));
    this.store.dispatch(new fromSabhaActions.SelectRoomAction(roomId));
  }
}
